import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { ToolbarService } from '../../services/toolbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ScannerService } from '../../services/scanner.service';
import { TourStop } from '../../models/tour-stop';
import { Subscription } from 'rxjs';
import { EventService } from '../../services/event.service';
import { MessageService } from '../../services/message.service';
import { TourService } from '../../services/tour.service';
import { BeepService } from '../../services/beep.service';
import { Message } from '../../models/message';
import { ModalService } from '../../services/modal.service';
import { AError } from '../../models/aerror';
import { AppUrls } from '../../app.urls';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AngularFireRemoteConfig } from '@angular/fire/compat/remote-config';
import {LocationService} from '../../services/location.service';
import {LocationAwareComponent} from '../location-aware/location-aware.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent extends LocationAwareComponent implements OnInit, OnDestroy {
  private scanResultSubscription: Subscription;
  private tourChangedSubscription: Subscription;

  private subsequentDeliveriesSubscription: Subscription;
  public subsequentDeliveriesBarcode: string;

  // set class on host element
  @HostBinding('class') classes = 'app-content';

  constructor(
    private router: Router,
    private events: EventService,
    private messageService: MessageService,
    private scanner: ScannerService,
    private beep: BeepService,
    private translate: TranslateService,
    private modal: ModalService,
    private changeDetectorRef: ChangeDetectorRef,
    private remoteConfig: AngularFireRemoteConfig,
    public tourService: TourService,
    public toolbar: ToolbarService
  ) {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.scanResultSubscription = this.scanner.scanResult.subscribe((value) => {
      this.didScan(value.data);
    });
    this.tourChangedSubscription = this.events.tourChanged.subscribe(() => {
      this.changeDetectorRef.detectChanges();
    });
    this.subsequentDeliveriesSubscription =
      this.remoteConfig.strings.subsequentDeliveriesBarcode.subscribe(
        (barcode) => {
          console.log(
            `New barcode for subsequent deliveries received: "${barcode}"`
          );
          this.subsequentDeliveriesBarcode = barcode;
        }
      );
    this.setupToolbar();
    this.tourService.refresh();

    if (!this.tourService.isStarted) {
      this.messageService.add(
        Message.info(this.translate.instant('home.scanTourStart'))
      );
    }

    this.scanner.resumeScanning();
  }

  override ngOnDestroy(): void {
    this.tourChangedSubscription.unsubscribe();
    this.scanResultSubscription.unsubscribe();
    this.subsequentDeliveriesSubscription.unsubscribe();

    this.messageService.clear();
    super.ngOnDestroy();
  }

  private didScan(barcode: string): void {
    if (!this.messageService.hasNotification) {
      this.scanner.pauseScanning();

      // handle scan
      if (!this.tourService.isStarted) {
        this.tryToStartTour(barcode).then((isSuccessful) =>
          isSuccessful ? this.beep.beep() : this.beep.bop()
        ).finally(() => this.scanner.resumeScanning());
      } else if (this.tourService.isEndBarcode(barcode)) {
        this.beep.beep();
        this.tryToEndTour(barcode);
      } else {
        this.tryToStartOrder(barcode).then((isSuccessful) =>
          isSuccessful ? this.beep.beep() : this.beep.bop()
        ).finally(() => {
          this.scanner.resumeScanning();
        })
      }
    }
  }

  get tourStops(): TourStop[] {
    return this.tourService.tourStops.filter(
      (tourStop) => !tourStop.isFinished
    );
  }

  // ===== Tour operations ===== //

  private tryToStartTour(barcode: string): Promise<boolean> {
    return this.tourService
      .startTour(barcode)
      .then(() => {
        this.changeDetectorRef.detectChanges();
        this.messageService.clear();
        this.setupToolbar();
        return true;
      })
      .catch((error) => {
        this.showError(AError.fromSomeError(error));
        this.setupToolbar();
        return Promise.resolve(false);
      });
  }

  private tryToEndTour(barcode: string): void {
    const endTour = () => {
      return this.tourService.endTour(barcode)
        .then(() => this.messageService.add(Message.success(this.translate.instant('home.tourEnded'))))
        .catch((someError) => {
          const error = AError.fromSomeError(someError);
          this.modal
            .showConfirmationModal(
              this.translate.instant('home.endTourErrorPromptTitle'),
              this.translate.instant('home.endTourErrorPromptText', {
                errorMessage: error.humanMessage,
              }),
              this.translate.instant('home.endTourErrorPromptOK'),
              this.translate.instant('cancel')
            )
            .then((result) => {
              if (result) {
                this.tourService.clearTour().then(() => {
                  this.messageService.add(Message.success(this.translate.instant('home.tourEnded')));
                });
              }
            });
        });
    }

    this.messageService.clear();

    if (this.tourService.isComplete) {
      endTour().finally(() => this.scanner.resumeScanning());
    } else {
      this.modal.showConfirmationModal(undefined, this.translate.instant('home.endTourPrompt'))
        .then((result) => {
          if (result) {
            endTour().finally(() => this.scanner.resumeScanning());
          } else {
            this.scanner.resumeScanning();
          }
        });
    }
  }

  private tryToStartOrder(barcode: string): Promise<boolean> {
    return this.tourService
      .startTransport(barcode)
      .then((transport) => {
        this.tourService.currentTransport = transport;
        this.tourService.needsTransportStartCode = false;
        return this.router.navigate([AppUrls.ROOT, AppUrls.TransportDetails]);
      })
      .catch((error) => {
        this.showError(AError.fromSomeError(error));
        this.scanner.resumeScanning();
        return Promise.resolve(false);
      });
  }

  // ===== Private helpers ===== //

  private showError(error: AError): void {
    this.messageService.clear();
    this.messageService.add(Message.warning(error.humanMessage));
  }

  private setupToolbar(): void {
    this.toolbar.setToolbarConfig({
      title: this.translate.instant('app.name'),
      backAction: undefined,
      showRefresh: this.tourService.isStarted,
      showSettings: true,
      showCall: true,
      showLogout: false,
    });
  }

  openTransportPlan(): void {
    this.router.navigate([AppUrls.ROOT, AppUrls.TransportPlan]);
  }

  // DISPO-63: A subsequent delivery is triggered by scanning a fixed Leitcode, all logic is done in the backend
  openSubsequentDeliveries(): void {
    this.didScan(this.subsequentDeliveriesBarcode);
  }
}
